import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { savePrayerRequest } from "../../services/PrayerServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DynamicFormRenderer, {
  useDynamicFormValidation,
} from "../../util/DynamicFormRenderer";
import {
  faUser,
  faEnvelope,
  faPhone,
  faGlobe,
  faMap,
  faHeadset,
  faUsers,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../../auth/AuthContext";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import UtilMethods from "../../util/UtilMethods";
import { sendEmail } from "../../services/EmailSender";
import { useLocation, useNavigate } from "react-router-dom";
import AlertModal from "../common/AlertModal"; // Import the reusable AlertModal component

import ConsentModal from "../common/ConsentModal";
import {
  loginUser,
  fetchSabbathDetails,
  registerSabbath,
} from "../../services/AdminServices";
import { registerRetreat } from "../../services/RetreatServices"; // Import the registerRetreat function
import { assignMeetingId } from "../../services/ZoomService"; // Import the assignMeetingId function
import countryStateData from "../../source/countryStateData"; // Adjust the import path as necessary
import {
  getRetreatDetails,
  getMandatoryFields,
} from "../../util/RetreatFieldsConfig"; // Adjust path as needed

const RetreatRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const { user, userType, logout, login } = useAuth();

  // Get URL params as usual

  const emailEncoded = searchParams.get("email");
  const phoneEncoded = searchParams.get("phone");
  const idVar = searchParams.get("id") || "GA"; // Default to "GA" if not provided
  const retreatDateString = searchParams.get("date") || "NA"; // Default date for the retreat

  //const emailIDVar = emailEncoded ? base64Decode(emailEncoded) : null;
  //const phoneVar = phoneEncoded ? base64Decode(phoneEncoded) : null;

  const emailIDVar = emailEncoded;
  const phoneVar = phoneEncoded;
  const [recordCategory, setRecordCategory] = useState(null); // State to hold the record category

  const [showConfirmModal, setShowConfirmModal] = useState(false); // state to control modal visibility
  const [showSuccessModal, setShowSuccessModal] = useState(false); // state to control modal visibility
  const [showConsentModal, setShowConsentModal] = useState(false); // Modal visibility state
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false); // state to control alert visibility
  const [modalType, setModalType] = useState("sabbath"); // Default type
  const [showCancelModal, setShowCancelModal] = useState(false); // state to control modal visibility
  const location = useLocation();
  const navigate = useNavigate();
  const record = {};

  const [isApproving, setIsApproving] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidCategory, setInvalidCategory] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");
  const isGuest = userType === "G";
  const retreatDetails = getRetreatDetails(idVar);
  const defaultImagePath = `${process.env.PUBLIC_URL}/images/Retreat_${idVar}.jpg`;
  const hasBrochure = retreatDetails?.details?.brochure;

  const htmlContent = `
  <div style="margin:auto;">
    <h4 style="text-align:center;">${retreatDetails?.details?.WOG || ""}</h4>
    ${retreatDetails?.details?.description || ""}
    <div className="text-center">
      ${retreatDetails?.details?.notes || ""}
      ${
        retreatDetails?.details?.email
          ? `For any questions or clarifications, please reach out to us at 
              <a href="mailto:${retreatDetails?.details?.email}">
                ${retreatDetails?.details?.email}
              </a>. `
          : ""
      }
      ${
        hasBrochure
          ? ` >> <a href="${defaultImagePath}" target="_blank" rel="noopener noreferrer">
               Click here for the brochure
             </a>`
          : ""
      }
    </div>
  </div>
`;

  const isAdmin = userType?.includes("A") || userType?.includes("S"); // Check if the user is an admin or super admin

  const mandatoryFields = getMandatoryFields(idVar) || [];

  // Update validateForm to use mandatoryFields
  const validateForm = () => {
    console.log("Validating form with mandatory fields:", mandatoryFields);
    const errors = {};
    const fieldValidationRules = retreatDetails?.fieldValidationRules || {};

    mandatoryFields.forEach((field) => {
      const value = formData[field];
      const rules = fieldValidationRules[field];

      if (!value) {
        errors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      } else if (rules) {
        if (rules.pattern && !new RegExp(rules.pattern).test(value)) {
          errors[field] = rules.message || `${field} is invalid`;
        }
        if (rules.minLength && value.length < rules.minLength) {
          errors[field] = rules.message || `${field} is too short`;
        }
        if (rules.maxLength && value.length > rules.maxLength) {
          errors[field] = rules.message || `${field} is too long`;
        }
      }
    });

    setFormData((prevState) => ({
      ...prevState,
      errors,
    }));
    console.log("Validation errors:", errors);
    return errors;
  };
  const getFieldValue = (field, defaultValue = "") => {
    if (record?.[field]) {
      return record[field];
    } else if (!(isAdmin || isGuest) && user?.[field]) {
      return user[field];
    } else {
      return defaultValue;
    }
  };

  const [formData, setFormData] = useState({
    retreatID: idVar,
    registrationID: "",
    zoomID: "",
    zoomInvite: "N",
    joinUrl: "",
    firstName: getFieldValue("firstName"),
    lastName: getFieldValue("lastName"),
    email: emailIDVar || getFieldValue("email"),
    phone: phoneVar || getFieldValue("phone"),
    address: getFieldValue("address"),
    city: getFieldValue("city"),
    state: getFieldValue("state"),
    zip: getFieldValue("zip"),
    country: getFieldValue("country"),
    sabbathGroup: retreatDetails?.details?.group || "US",
    participantCount: "1",
    status: "New",
    category: idVar || "Online Retreat",
    type: retreatDetails?.details?.type || idVar || "Online Retreat",
    errors: {},
    submittedData: null,
    isSubmitting: false,
    consentToUseData: false,
    remarks: "", // Add remarks field
    retreatDateString: retreatDateString, // Default date for the retreat
    participantCount: "1",
    zoomMeetingID: "",
  });

  const alertTimeoutRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Skip if form is submitting or recordCategory is set (i.e., already submitted or saved)
      if (
        formData.isSubmitting ||
        (recordCategory && recordCategory.retreatID)
      ) {
        return;
      }
      // Logout guest user on form exit
      if (user && user.email === process.env.REACT_APP_GUEST_EMAIL) {
        logout();
      }
    };
    // Listen to the "beforeunload" event to warn the user if the form is dirty
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Cleanup function to remove the event listener when component is unmounted
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formData, recordCategory, user, logout]); // Dependencies

  useEffect(() => {
    console.log("RetreatRegistrationPage mounted");
    console.log("emailIDVar", emailIDVar);
    console.log("phoneVar", phoneVar);
    console.log("idVar", idVar);
    //console.log("retreatDetails", retreatDetails);

    if (!idVar || !retreatDetails || !retreatDetails.details) {
      setInvalidCategory(true);
      return;
    } else {
      setInvalidCategory(false);
    }
    const fetchData = async () => {
      setInvalidEmail(false); // Reset invalid email state before fetching
      if (!user) {
        const response = await loginUser(
          process.env.REACT_APP_GUEST_EMAIL,
          process.env.REACT_APP_GUEST_PASSWORD
        );
        console.log("Login response:", response);
        if (!response.user || !response.token) {
          throw new Error("Invalid login response. User or token missing.");
        }
        await login({ userData: response.user, token: response.token });
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailIDVar]);

  const cancelSubmit = () => {
    setFormData((prevState) => ({ ...prevState, isSubmitting: false })); // Reset isSubmitting state
    setShowConfirmModal(false); // Close the modal without submitting
  };
  // Handle field changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  // Function to handle the link click and show the modal
  const handleShowConsentModal = () => {
    setModalType("sabbath"); // Set the appropriate type
    setShowConsentModal(true); // Show the modal
  };

  // Function to close the modal
  const handleCloseConsentModal = () => {
    setShowConsentModal(false); // Hide the modal
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
  };

  async function saveFormPrayerRequest(formData) {
    const emailToUse = formData.email || "prayer@graceanointing.org";
    const prayerData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: emailToUse,
      message: formData.prayerIntentions,
      category: formData.category,
    };
    const response = await savePrayerRequest(prayerData);

    return response;
  } // Export the functions
  // Function to close the modal
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false); // Hide the modal
    if (user && user.email === process.env.REACT_APP_GUEST_EMAIL) {
      // If the user is a guest, log them out
      console.log("handleCancel Retreat Retreat, logout GUEST MODE");
      logout();
    }
    navigate("/", { state: { record: {} } });
    resetForm();
  };

  // Handle radio button change for category selection
  const handleCheckChange = (e) => {
    setFormData({
      ...formData,
      consentToUseData: e.target.checked,
    });
  };

  // Phone change handler
  const handlePhoneChange = (phone) => {
    setFormData((prevState) => ({
      ...prevState,
      phone: phone,
    }));
  };

  // Form validation
  // (Removed duplicate definition; using the version above that uses mandatoryFields)
  // Handle 'Approve' button click

  // Show modal and set formData before submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    console.log("handleSubmit errors:", errors);
    if (Object.keys(errors).length === 0) {
      setFormData((prevState) => ({
        ...prevState,
        isSubmitting: true,
      }));
      setShowConfirmModal(true); // Show the modal
    } else {
      showAlert(
        "Please fill in all required fields: " + Object.values(errors)[0],
        "danger"
      );
    }
  };
  // Handle form submission after modal confirmation
  const confirmSubmit = async () => {
    const { address, city, state, zip, country } = formData;
    const fullAddress = `${address}, ${city}, ${state}, ${zip}, ${country}`;
    try {
      // API request to submit the form
      console.log(" /n Retreat/register formData", formData);
      let response;
      setShowConfirmModal(false); // Close the modal after submitting
      // Set the zoomMeetingID in formData if available in the response

      // Compute zoomMeetingID before submitting
      const zoomMeetingID = assignMeetingId(
        formData.category,
        formData.sabbathGroup
      );

      response = await registerRetreat({
        ...formData,
        zoomMeetingID,
      });

      // On success, set saved data and clear error
      console.log(" save response ", response);
      if (response.data.retreat) {
        setFormData((prevState) => ({
          ...prevState,
          registrationID: response.data?.retreat?.registrationID,
        }));
        console.log(
          " response.data.retreat ",
          response.data?.retreat?.registrationID
        );

        if (
          formData.email &&
          retreatDetails &&
          retreatDetails.details &&
          retreatDetails.details.emailJS
        ) {
          const emailResponse = await sendEmail(
            {
              ...formData,
              registrationID: response.data?.retreat?.registrationID,
            },
            retreatDetails?.details?.emailJS,
            "service_retreat"
          );
          console.log(
            " emailResponse.error?.text #",
            emailResponse.error?.text
          );
          if (emailResponse.success) {
            setSuccessMessage(
              "Retreat Registration Received,You will receive an email with the details."
            );
            setShowSuccessModal(true); // Show the success modal
            showAlert(
              "Retreat Registration Received,You will receive an email with the details.",
              "success"
            );
          } else {
            if (emailResponse.error?.status === 422) {
              showAlert(
                `Failed to send email: ${emailResponse.error?.text || ""}`,
                "danger"
              );
            } else {
              showAlert("Failed to send email", "danger");
            }
          }
        }
        console.log(" prayerIntentions ", formData.prayerIntentions);
        setSuccessMessage("Retreat Registration Received Successfully.");
        setFormData((prevState) => ({
          ...prevState,
          isSubmitting: false,
        }));
        setShowSuccessModal(true); // Show the success modal
      } else if (response.data.savedData) {
        setSuccessMessage("Retreat Registration Received Successfully.");
        setFormData((prevState) => ({
          ...prevState,
          isSubmitting: false,
        }));
        setShowSuccessModal(true); // Show the success modal
      }

      //setFormData(response.data.savedData);
      setError("");
    } catch (error) {
      console.error("Error submitting form:", error.code);
      console.log(" sabbath/register response error ", error);
      let errorMessage = "Error submitting form. Please try again.";
      // Check if there is a response from the backend with specific error details
      if (error?.code === 409) {
        console.log(
          "409 error sabbath/register response error ",
          error?.details?.message
        );
        errorMessage = error?.details?.message;
      } else if (
        error?.response &&
        error?.response?.data &&
        error?.response?.data?.details
      ) {
        // Display detailed error from the backend if available
        errorMessage =
          error?.response?.data?.details ||
          "Error submitting form. Please try again.";
      } else if (
        error?.response &&
        error?.response?.data &&
        error?.response?.data?.error
      ) {
        // Fallback to a generic error message if no specific details are provided
        errorMessage =
          error?.response?.data?.error ||
          "Error submitting form. Please try again.";
      } else {
        // General fallback message if backend doesn't provide a structured response
        errorMessage = "Error submitting form. Please try again.";
      }
      setErrorMessage(errorMessage);
      showAlert(errorMessage, "danger");
      setShowErrorModal(true); // Show the error modal
      setFormData((prevState) => ({
        ...prevState,
        isSubmitting: false,
      }));
    }
  };

  // Alert management
  const [alert, setAlert] = useState({
    message: "",
    type: "",
  });

  const showAlert = (message, type) => {
    setAlert({ message, type });

    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);

    alertTimeoutRef.current = setTimeout(() => {
      setAlert({ message: "", type: "" });
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    };
  }, []);

  // Reset form fields and errors
  const resetForm = () => {
    setFormData({
      record: {},
      id: null,
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      category: idVar || "Online Retreat",
      type: retreatDetails?.details?.type || category,
      participantCount: "1",
      sabbathGroup: retreatDetails?.details?.group || "US",
      // Reset sabbathGroup field
      consentToUseData: false,
      errors: {},
      submittedData: null,
      isSubmitting: false,
    });
  };

  const confirmCancel = () => {
    navigate("/", { state: { record: {} } });
    if (user && user.email === process.env.REACT_APP_GUEST_EMAIL) {
      logout();
    }
    resetForm();
    setShowCancelModal(false);
  };
  const handleCancel = () => {
    setFormData((prevState) => ({
      ...prevState,
      isSubmitting: false,
    }));
    setShowCancelModal(true);
  };
  const cancelCancel = () => {
    setFormData((prevState) => ({
      ...prevState,
      isSubmitting: false,
    }));
    setShowCancelModal(false);
  };
  const {
    id,
    zoomID,
    zoomInvite,
    joinUrl,
    firstName,
    lastName,
    email,
    phone,
    address,
    city,
    state,
    zip,
    country,
    category,
    type,
    sabbathGroup,
    consentToUseData,
    errors,
    isSubmitting,
  } = formData;

  return (
    <React.Fragment>
      {/* Top Menu */}
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      {/* Banner */}
      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
          role="img"
          aria-label="Retreat Banner"
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">
              🕊️ {retreatDetails?.details?.name || "Retreat"} 🕊️
            </h1>
          </div>
        </div>
      </div>

      {/* Invalid Category Handling */}
      {invalidCategory ? (
        <div className="container-fluid mt-2">
          <div className="alert alert-danger text-center">
            <strong>Invalid Retreat Category.</strong> Please check your
            registration link or contact&nbsp;
            <a href="mailto:retreat@graceanointing.org">
              retreat@graceanointing.org
            </a>{" "}
            for assistance.
          </div>
        </div>
      ) : (
        <div className="container-fluid mt-2">
          {/* Heading with Icons */}
          <div className="d-flex justify-content-center align-items-center my-3">
            <img
              src={`${process.env.PUBLIC_URL}/images/Divine Mercy.png`}
              alt=""
              style={{ maxWidth: "50px", height: "auto", marginRight: "10px" }}
            />
            <h3 className="mb-0">Walk Through the Way of Grace Anointing</h3>
            <img
              src={`${process.env.PUBLIC_URL}/images/Rosa Mystica.png`}
              alt=""
              style={{ maxWidth: "50px", height: "auto", marginLeft: "10px" }}
            />
          </div>

          {/* Dynamic HTML */}
              <div className="text-center mb-1"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {/* Already Registered Alert */}
          {recordCategory && recordCategory.retreatID && (
            <div className="alert alert-info align-items-center">
              <p className="alert alert-success mb-1 text-center">
                <strong>
                  🌿 You are already Registered with below details 🌿
                </strong>
                <br />
                <strong>Registration ID:</strong> {recordCategory.type} :{" "}
                {recordCategory.retreatID}
                {recordCategory.joinUrl && (
                  <>
                    <strong> | URL:</strong>{" "}
                    <a
                      href={recordCategory.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {"👉 Click Here to Join the Retreat on "}{" "}
                      {recordCategory.retreatDateString}
                    </a>
                  </>
                )}
              </p>
            </div>
          )}

          {/* Registration Form */}
          {(() => {
            const now = new Date();
            const regStart = retreatDetails?.details?.reg_start_date
              ? new Date(retreatDetails.details.reg_start_date)
              : null;
            const regEnd = retreatDetails?.details?.reg_end_date
              ? new Date(retreatDetails.details.reg_end_date)
              : null;

            const alreadyRegistered = !!(
              recordCategory && recordCategory.registrationID
            );

            if (regStart && now <= regStart) {
              return (
                <div className="alert alert-warning text-center">
                  Registration will open on {regStart.toLocaleDateString()}.
                </div>
              );
            } else if (regEnd && now >= regEnd) {
              return (
                <div className="alert alert-danger text-center align-items-center">
                  Registration is closed.
                </div>
              );
            } else if (
              (regStart && regEnd && now > regStart && now < regEnd) ||
              (!regStart && !regEnd)
            ) {
              return (
                <form onSubmit={handleSubmit}>
                  <DynamicFormRenderer
                    formData={formData}
                    onFormDataChange={setFormData}
                    config={retreatDetails?.fields}
                    category={idVar}
                    isAdmin={isAdmin}
                    isGuest={isGuest}
                  />

                  {/* Buttons */}
                  <div className="d-flex justify-content-around gap-4 mb-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn btn-light flex-grow-1"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary flex-grow-1"
                      title={
                        alreadyRegistered
                          ? "Already Registered !!!"
                          : "Submit your registration"
                      }
                      disabled={isSubmitting || alreadyRegistered}
                    >
                      {isSubmitting ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : alreadyRegistered ? (
                        "Already Registered !!!"
                      ) : (
                        "Submit"
                      )}
                      <Tooltip
                        id="submitTooltip"
                        place="top"
                        content={
                          alreadyRegistered
                            ? "Already Registered !!!"
                            : "Submit your registration"
                        }
                      />
                    </button>
                  </div>

                  {/* Approval Message */}
                  {approvalMessage && (
                    <div className="mt-2">
                      <p>{approvalMessage}</p>
                    </div>
                  )}

                  {/* Closing Verse */}
                  <h4 className="text-center">
                    <i>
                      "Wherever they go, they become the fragrance of Christ"
                    </i>{" "}
                    - 2 Cor 2:15
                  </h4>
                </form>
              );
            }
            return null;
          })()}

          {/* Alert Message */}
          {alert.message && (
            <div className={`alert alert-${alert.type} mt-4`} role="alert">
              {alert.message}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <AlertModal
        show={showConfirmModal}
        title="Confirm Retreat Request"
        type="confirmation"
        message="Are you sure you want to register for Retreat, with below details?"
        details={{
          "First Name": firstName,
          "Last Name": lastName,
          Email: email,
          Phone: phone,
        }}
        onCancel={cancelSubmit}
        onConfirm={confirmSubmit}
      />

      {/* Cancel Modal */}
      <AlertModal
        show={showCancelModal}
        title="Cancel Retreat Request"
        type="confirmation"
        message="Are you sure you want to navigate away from the Retreat Registration?"
        details={{}}
        onCancel={cancelCancel}
        onConfirm={confirmCancel}
      />

      {/* Success Modal */}
      <AlertModal
        show={showSuccessModal}
        title="Retreat Registration Successful"
        message={successMessage}
        type="success"
        details={{}}
        onCancel={handleCloseSuccessModal}
      />

      {/* Error Modal */}
      <AlertModal
        show={showErrorModal}
        title="Registration Failed"
        message={errorMessage}
        type="error"
        details={{}}
        onCancel={handleCloseErrorModal}
      />

      {/* Consent Modal */}
      <ConsentModal
        show={showConsentModal}
        type={modalType}
        onCancel={handleCloseConsentModal}
      />

      <FooterPanel />
    </React.Fragment>
  );
};

export default RetreatRegistrationPage;
